'use client';

import * as stylex from '@stylexjs/stylex';
import { Field as BaseField } from '@base-ui/react/field';
import '../../../../styles/tokens.stylex';
import { spacing, typographyColor } from '../../../../styles/tokens.stylex';
import { typography } from '../../../../styles/typography';
import { formStyles } from '../../form.styles';
import '../../form.tokens.stylex';
import { formTokens } from '../../form.tokens.stylex';
import { inputIconMarker, labelMarker } from '../../markers.stylex';
import Image from 'next/image';
import { createContext, use, useMemo } from 'react';
import { FieldError, FieldValues, RefCallBack, useController, UseControllerProps } from 'react-hook-form';
type BaseUICompositeType<T> = {
  styles?: stylex.StyleXStyles;
} & T;
export const FieldControlContext = createContext({
  value: undefined,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onBlur: (event: React.FocusEvent<HTMLElement>) => {},
  onValueChange: (...args: unknown[]) => {},
  ref: (() => {}) as RefCallBack,
  name: ''
});
export const FieldStateContext = createContext({
  error: undefined as FieldError | undefined,
  isDirty: false,
  isTouched: false,
  isError: false,
  isSuccess: false
});
export function Root(props: BaseUICompositeType<BaseField.Root.Props> & UseControllerProps<FieldValues> & {
  onBlur?: (event: React.FocusEvent<HTMLElement>) => void;
}) {
  const {
    onBlur
  } = props;
  const {
    field,
    fieldState
  } = useController(props);
  //console.log('fieldState', fieldState)
  const isError = !!fieldState.error;
  const isSuccess = !isError && fieldState.isDirty && fieldState.isTouched;
  const fieldControlContext = useMemo(() => ({
    value: field.value,
    onBlur: (event: React.FocusEvent<HTMLElement>) => {
      field.onBlur();
      onBlur?.(event);
    },
    onValueChange: field.onChange,
    ref: field.ref,
    name: field.name
  }), [field, onBlur]);
  const fieldStateContext = useMemo(() => ({
    error: fieldState.error,
    isDirty: fieldState.isDirty,
    isTouched: fieldState.isTouched,
    isError,
    isSuccess
  }), [fieldState.error, fieldState.isDirty, fieldState.isTouched, isError, isSuccess]);
  return <FieldControlContext.Provider value={fieldControlContext}>
      <FieldStateContext.Provider value={fieldStateContext}>
        <BaseField.Root {...props} {...stylex.props(sx.root, isError && sx.error, props.styles)} />
      </FieldStateContext.Provider>
    </FieldControlContext.Provider>;
}
export function Label(props: BaseUICompositeType<BaseField.Label.Props>) {
  return <BaseField.Label {...props} {...stylex.props(labelMarker, typography.label, formStyles.label, props.styles)} />;
}
export function Control(props: BaseUICompositeType<BaseField.Control.Props>) {
  const fieldControl = use(FieldControlContext);
  return <BaseField.Control {...fieldControl} {...props} {...stylex.props(typography.body, formStyles.input, props.styles)} />;
}
export function Description(props: BaseUICompositeType<BaseField.Description.Props>) {
  if (!props.children) return null;
  return <BaseField.Description {...props} {...stylex.props(typography.small, sx.description, props.styles)} />;
}
export function Item(props: BaseUICompositeType<BaseField.Item.Props>) {
  return <BaseField.Item {...props} {...stylex.props(sx.item, props.styles)} />;
}
export function Error(props: BaseUICompositeType<BaseField.Error.Props>) {
  const ctx = use(FieldStateContext);
  if (!ctx.isError) return null;
  return <BaseField.Error {...stylex.props(typography.small, sx.errorMessage, props.styles)} {...props} match={true}>
      <p>{ctx.error?.message}</p>
    </BaseField.Error>;
}
export function Validity(props: BaseUICompositeType<BaseField.Validity.Props>) {
  return <BaseField.Validity {...props} {...stylex.props(sx.validity, props.styles)} />;
}
type FieldIconProps = {
  styles?: stylex.StyleXStyles;
  src: string;
};
export function Icon({
  styles,
  src
}: FieldIconProps) {
  return <div {...stylex.props(inputIconMarker, formStyles.inputIconWrapper, styles)}>
      <Image src={src} alt="" fill sizes="48px" className="x19kjcj4" />
    </div>;
}
export const Field = {
  Root,
  Label,
  Control,
  Description,
  Item,
  Error,
  Validity,
  Icon
};
const sx = {
  root: {
    kOIVth: "x3ybb33",
    k1xSpc: "x78zum5",
    kXwgrk: "xdt5ytf",
    $$css: true
  },
  error: {
    "--input_root": "x1kayhiy",
    $$css: true
  },
  errorMessage: {
    kMwMTN: "x1vdqsxu",
    $$css: true
  },
  description: {
    kMwMTN: "xcgynuj",
    $$css: true
  },
  item: {
    k1xSpc: "xrvj5dj",
    $$css: true
  },
  validity: {
    $$css: true
  }
};